/* eslint-disable react-hooks/rules-of-hooks */
'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import { del, get, post, put } from './apiService'

export function useApiReactQuery() {
  const queryClient = useQueryClient()
  const [error, setError] = useState(null)
  const latestKeyRef = useRef(null)

  const queryGet = (
    key,
    url,
    authRequired = false,
    version = null,
    options = {},
  ) => {
    latestKeyRef.current = key

    const query = useQuery({
      queryKey: [key],
      queryFn: () => get(url, authRequired, version),
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Set to false to prevent unnecessary refetches when switching browser tabs
      // This improves UX by showing cached data immediately instead of loading skeletons
      // Data will still refetch when staleTime expires or query params change
      refetchOnWindowFocus: false,
      ...options,
      onError: (err) => {
        setError(err)
        options?.onError?.(err)
      },
    })

    return query
  }

  const useGenericMutation = (mutationFn) => {
    return useMutation({
      mutationFn,
      onError: (err) => setError(err),
    })
  }

  const postMutation = useGenericMutation((params) =>
    post(params.url, params.data, params.authRequired, params.version),
  )
  const putMutation = useGenericMutation((params) =>
    put(params.url, params.data, params.authRequired, params.version),
  )
  const deleteMutation = useGenericMutation((params) =>
    del(params.url, params.authRequired, params.version),
  )

  const reset = useCallback(() => {
    if (latestKeyRef.current) {
      queryClient.removeQueries({ queryKey: [latestKeyRef.current] })
    }
    setError(null)
  }, [queryClient])

  return {
    get: queryGet,
    post: postMutation,
    put: putMutation,
    del: deleteMutation,
    error,
    reset,
  }
}
